import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import { FormFooter } from 'components/form/FormLayout.styled';
import Authorization from 'components/global/Authorization';
import FormHeader from 'components/global/FormHeader';
import Typography from 'components/global/Typography';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import BranchFormshort from 'pages/Branches/BranchFormShort';
import WarehouseFormShort from 'pages/Warehouses/WarehouseFormShort';

import { AUTOCOMPLETE, ENUMS, PROFILE } from 'services/consts';

import { setUserLocalization } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

import * as S from '../Profile/ProfileSettings.styled';
import MapSettingsModal from './MapSettingsModal';

const warehouseAutocompleteColumns = [
	{ accessor: 'Name', width: 200 },
	{ accessor: 'code', width: 150 }
];
const warehouseAutocompleteLabels = ['Name'];
const branchAutocompleteColumns = [{ accessor: 'Name', width: 200 }];
const branchAutocompleteLabels = ['Name'];

const defaultColSpan = {
	lg: 8,
	xl: 7
};

const validateOptions = { shouldDirty: true, shouldValidate: true };

const safeParse = (value) => {
	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
};

const Localization = () => {
	const axios = useAxiosPrivate();
	const isAuthorized = useAccessControl();

	const [isLoading, setIsLoading] = useState(false);
	const { showSuccess, showError } = useAlert();
	const [ID, setID] = useState();
	const dispatch = useDispatch();

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					timeZone: yup.string().nullable().required(),
					dateFormat: yup.string().nullable().required(),
					PreperationSLA: yup
						.number()
						.nullable()
						.when([], {
							is: () => isAuthorized('SLAModule'),
							then: (schema) => schema.required().min(1),
							otherwise: (schema) => schema
						}),
					defaultWarehouse: yup.number().nullable(),
					defaultBranch: yup.number().nullable()
				})
				.required(),
		[]
	);

	// hook form
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			countryISO2: null,
			timeZone: null,
			currency: null,
			dateFormat: null,
			PreperationSLA: null,
			defaultWarehouse: null,
			defaultBranch: null
		}
	});

	const {
		handleSubmit,
		reset,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	const getLocalizationSettings = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await axios.post(PROFILE.GET_LOCALIZATION_SETTINGS);

			if (response.status === 200) {
				setID(response.data.ID);
				reset({
					countryISO2: response.data.countryISO2 ?? null,
					timeZone: response.data.timeZone ?? null,
					currency: response.data.currency ?? null,
					dateFormat: response.data.dateFormat ?? null,
					PreperationSLA: response.data.PreperationSLA ?? null,
					defaultWarehouse: response.data.defaultWarehouse ?? null,
					defaultBranch: response.data.defaultBranch ?? null,
					mapSettings: safeParse(response.data?.mapSettings)
				});
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	}, [reset]);

	useEffect(() => {
		getLocalizationSettings();
	}, []);

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);
	const onSubmit = async (data) => {
		setIsLoading(true);
		const body = { ...data };
		delete body.mapSettings;
		try {
			const response = await axios.post(PROFILE.UPDATE_LOCALIZATION_SETTINGS, {
				...body,
				ID
			});
			if (response.status === 200) {
				showSuccess();
				dispatch(
					setUserLocalization({
						timeZone: data.timeZone,
						dateFormat: data.dateFormat
					})
				);
				reset({
					...data
				});
			} else {
				AppLogger.error(response);
				setIsLoading(false);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<S.Container>
			<HeaderContainerFormHeader className="settingsMaxWidth settingsFormHeader">
				<FormHeader boldTitle="configuration" />
				<S.ButtonContainer>
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty || isLoading}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						icon={faFloppyDisk}
						label="save"
						variant="blue"
						className="saveButton"
					/>
				</S.ButtonContainer>
			</HeaderContainerFormHeader>

			<S.StyledForm formMethods={formMethods} borderless>
				<CountryAndRegionSection isLoading={isLoading} />

				<Authorization path={['SLAModule', 'MultiWarehouseModule', 'BranchModule']}>
					<WarehouseAndSLASection isLoading={isLoading} />
				</Authorization>

				<MapViewSection isLoading={isLoading} ID={ID} />
			</S.StyledForm>

			<FormFooter>
				<Button
					lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty || isLoading}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					icon={faFloppyDisk}
					label="save"
					variant="blue"
					className="saveButton"
				/>
			</FormFooter>
		</S.Container>
	);
};

const SectionTitle = ({ title, description, hasPadding = true }) => (
	<S.SectionTitleContainer className={!hasPadding && 'pb-0'}>
		<Typography variant="p2" fontWeight="500">
			{title}
		</Typography>
		<Typography color="var(--content-2)" noWrap>
			{description}
		</Typography>
	</S.SectionTitleContainer>
);

const CountryAndRegionSection = ({ isLoading }) => {
	const formMethods = useFormContext();
	const { t } = useTranslation();
	const { setValue, watch } = formMethods;

	return (
		<S.ConfigurationStyledCard className="settingsMaxWidth" fullWidth>
			<SectionTitle title="countryAndRegion" description="countryAndRegionDescription" />
			<S.FormContainer>
				<FormItem label="countryOrTerritory" colSpan={defaultColSpan} isLoading={isLoading}>
					<StringInput value={t(`country.${watch('countryISO2')}`)} disabled />
				</FormItem>

				<FormItem label="currency" colSpan={defaultColSpan} isLoading={isLoading}>
					<StringInput name="currency" disabled />
				</FormItem>

				<FormItem label="timeZone" colSpan={defaultColSpan} required isLoading={isLoading}>
					<EnumSelect
						data={Intl.supportedValuesOf('timeZone')}
						name="timeZone"
						value={watch('timeZone')}
						setValue={(value) => {
							setValue('timeZone', value, validateOptions);
						}}
						placeholder="selectOne"
						valueRemovable={false}
					/>
				</FormItem>

				<FormItem label="dateFormat" colSpan={defaultColSpan} required isLoading={isLoading}>
					<EnumSelect
						enumName={ENUMS.V2_DATE_FORMATS}
						name="dateFormat"
						value={watch('dateFormat')}
						setValue={(value) => {
							setValue('dateFormat', value, validateOptions);
						}}
						placeholder="selectOne"
						searchable={false}
						valueRemovable={false}
					/>
				</FormItem>
			</S.FormContainer>
		</S.ConfigurationStyledCard>
	);
};

const WarehouseAndSLASection = ({ isLoading }) => {
	const formMethods = useFormContext();
	const isAuthorized = useAccessControl();
	const { setValue, watch } = formMethods;

	const warehouseAutoComplete = watch('defaultWarehouse');
	const branchAutoComplete = watch('defaultBranch');

	return (
		<S.ConfigurationStyledCard className="settingsMaxWidth" fullWidth>
			<SectionTitle title="warehouseAndSLA" description="warehouseAndSLADescription" />
			<S.FormContainer>
				<FormItem
					colSpan={defaultColSpan}
					label="defaultWarehouse"
					isLoading={isLoading}
					roleName="MultiWarehouseModule"
					className="base-form-item">
					<AutoComplete
						name="defaultWarehouse"
						autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
						description="defaultWarehouse.description"
						columns={warehouseAutocompleteColumns}
						selectionLabelAccessor={warehouseAutocompleteLabels}
						value={warehouseAutoComplete}
						valueRemovable={false}
						firstItem={
							isAuthorized('/autocomplete/warehouse/addANewWarehouse')
								? {
										action: () =>
											NiceModal.show(WarehouseFormShort, {
												setWarehouse: (value) => {
													setValue(`defaultWarehouse`, value, validateOptions);
												}
											}),
										label: <Typography color="var(--blue)">addANewWarehouse</Typography>
									}
								: undefined
						}
						setValue={(value, isFirstRender) => {
							if (isFirstRender) return;
							setValue('defaultWarehouse', value?.ID, validateOptions);
						}}
					/>
				</FormItem>

				<FormItem
					label="preperationSLA"
					isLoading={isLoading}
					required
					roleName="SLAModule"
					className="base-form-item">
					<IntegerInput name="PreperationSLA" description="PreperationSLA.description" showArrows={false} />
				</FormItem>

				<FormItem
					colSpan={defaultColSpan}
					label="defaultBranch"
					isLoading={isLoading}
					className="base-form-item"
					roleName="BranchModule">
					<AutoComplete
						name="defaultBranch"
						autoCompleteName={AUTOCOMPLETE.BRANCHES}
						value={branchAutoComplete}
						columns={branchAutocompleteColumns}
						selectionLabelAccessor={branchAutocompleteLabels}
						valueRemovable={false}
						firstItem={
							isAuthorized('/autocomplete/branch/addANewBranch')
								? {
										action: () =>
											NiceModal.show(BranchFormshort, {
												setBranch: (value) => {
													setValue(`defaultBranch`, value, validateOptions);
												}
											}),
										label: <Typography color="var(--blue)">addANewBranch</Typography>
									}
								: undefined
						}
						setValue={(value, firstRender) => {
							if (firstRender) return;
							setValue('defaultBranch', value?.ID, validateOptions);
						}}
					/>
				</FormItem>
			</S.FormContainer>
		</S.ConfigurationStyledCard>
	);
};

const MapViewSection = ({ isLoading, ID }) => {
	const formMethods = useFormContext();
	const { watch, setValue } = formMethods;

	return (
		<S.ConfigurationStyledCard className="settingsMaxWidth" fullWidth>
			<S.ModulesContainer>
				<SectionTitle title="mapView" description="mapViewDescription" hasPadding={false} />
				<div>
					<Button
						variant="outline"
						label="configureMap"
						onClick={() =>
							NiceModal.show(MapSettingsModal, {
								ID,
								mapSettings: watch('mapSettings'),
								setMapValue: (value) =>
									setValue('mapSettings', value, {
										shouldDirty: false
									})
							})
						}
						loading={isLoading}
					/>
				</div>
			</S.ModulesContainer>
		</S.ConfigurationStyledCard>
	);
};

export default Localization;
